function formatTrackingDateTime(iso) {
  if (!iso) return { date: "—", time: "—" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: String(iso), time: "" };
  return {
    date: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
  };
}

function formatShortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function computeTat(sta, eta) {
  if (!sta || !eta) return "—";
  const start = new Date(sta);
  const end = new Date(eta);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";
  const days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
  return days === 1 ? "1 Day" : `${days} Days`;
}

function extractTollName(location) {
  if (!location) return "Toll Plaza";
  const primary = String(location).split(" - ")[0]?.trim();
  if (primary) return primary;
  return String(location).split(",")[0]?.trim() || "Toll Plaza";
}

function shortLabel(value, fallback = "—") {
  const s = value != null ? String(value).trim() : "";
  if (!s || s === "~") return fallback;
  return s.split(",")[0]?.trim() || s;
}

function cleanAddress(value) {
  const s = value != null ? String(value).trim() : "";
  if (!s || s === "~") return "";
  return s;
}

function isValidCoord(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng);
}

function normalizeProducts(products) {
  if (!Array.isArray(products)) return [];
  return products.map((p) => ({
    prodId: p.prodId ?? p.prod_id,
    productName: String(p.productName ?? p.product_name ?? "—").trim(),
    quantity: p.quantity ?? 0,
    uom: String(p.uom ?? "").trim(),
  }));
}

/** Map GET `/api/v1/tnt/get_trip_details` body → tracking modal view model. */
export function mapTripDetailsToTracking(detail, row) {
  if (!detail || typeof detail !== "object") {
    return {
      shipment: {
        vehicle: row?.vehicleNumber || "—",
        id: "—",
        tat: "—",
        sta: "—",
        eta: "—",
        totalKm: 0,
        remainingKm: 0,
        progress: 0,
        consignee: "—",
        consignor: "—",
        consigneeAddress: "",
        consignorAddress: "",
        ewbNumber: "—",
        ewbExpiry: "—",
        originPincode: "—",
        destinationPincode: "—",
        destination: "—",
        vehicleNumber: "—",
      },
      products: [],
      log: [],
      waypoints: [],
      livePosition: { title: "—", sub: "" },
      nextStop: { title: "—", sub: "" },
    };
  }

  const tollsDesc = [...(detail.tolls || [])].sort(
    (a, b) => new Date(b.trackedAt) - new Date(a.trackedAt),
  );
  const tollsAsc = [...tollsDesc].reverse();

  const totalKm = Number(detail.totalDistance) || 0;
  const coveredKm = Number(detail.totalDistanceCovered) || 0;
  const remainingKm = Math.max(0, totalKm - coveredKm);
  const progress = totalKm > 0 ? Math.min(100, Math.round((coveredKm / totalKm) * 100)) : 0;

  const origin = {
    id: "origin",
    type: "origin",
    label: shortLabel(detail.consignor, "Origin"),
    sub: detail.originPincode || "",
    location: cleanAddress(detail.consignorAddress),
    trackedAt: detail.sta || null,
    lat: detail.originLatitude,
    lng: detail.originLongitude,
    status: "done",
  };

  const destination = {
    id: "destination",
    type: "destination",
    label: shortLabel(detail.destination, "Destination"),
    sub: detail.destinationPincode || "",
    location: shortLabel(detail.destination, "Destination"),
    trackedAt: detail.eta || null,
    lat: detail.destinationLatitude,
    lng: detail.destinationLongitude,
    status: "pending",
  };

  const tollWaypoints = tollsAsc
    .filter((t) => isValidCoord(t.latitude, t.longitude))
    .map((t, i, arr) => ({
      id: t.tollId || `toll-${i}`,
      type: "toll",
      label: extractTollName(t.location),
      sub: "",
      location: t.location || "",
      trackedAt: t.trackedAt || null,
      lat: t.latitude,
      lng: t.longitude,
      status: i === arr.length - 1 ? "active" : "done",
    }));

  const waypoints = [origin, ...tollWaypoints, destination].filter((w) =>
    isValidCoord(w.lat, w.lng),
  );

  const tollLog = tollsDesc.map((t, i) => {
    const { date, time } = formatTrackingDateTime(t.trackedAt);
    return {
      date,
      time,
      location: extractTollName(t.location),
      notes: t.location || "",
      status: i === 0 ? "active" : "done",
    };
  });

  const log =  tollLog;

  const latestToll = tollsDesc[0];
  const livePosition = latestToll
    ? {
        title: extractTollName(latestToll.location),
        sub: latestToll.location || "",
        trackedAt: latestToll.trackedAt,
      }
    : {
        title: shortLabel(detail.consignor, "Origin"),
        sub: cleanAddress(detail.consignorAddress) || detail.originPincode || "",
        trackedAt: detail.sta,
      };

  const nextStop = {
    title: shortLabel(detail.destination, "Destination"),
    sub: `${remainingKm.toLocaleString("en-IN", { maximumFractionDigits: 2 })} km · ETA ${formatShortDate(detail.eta)}`,
  };

  return {
    shipment: {
      vehicle: detail.carrierName || row?.vehicleNumber || "—",
      id: `#${String(detail.tripId ?? "").replace(/^#/, "")}`,
      tat: computeTat(detail.sta, detail.eta),
      sta: formatShortDate(detail.sta),
      eta: formatShortDate(detail.eta),
      totalKm,
      remainingKm: Number(remainingKm.toFixed(2)),
      progress,
      consignee: shortLabel(detail.consignee, "—"),
      consignor: shortLabel(detail.consignor, "—"),
      consigneeAddress: cleanAddress(detail.consigneeAddress),
      consignorAddress: cleanAddress(detail.consignorAddress),
      ewbNumber: detail.ewbNumber || "—",
      ewbExpiry: formatShortDate(detail.ewbExpiry),
      originPincode: detail.originPincode || "—",
      destinationPincode: detail.destinationPincode || "—",
      destination: shortLabel(detail.destination, "—"),
      vehicleNumber: detail.vehicleNumber || " ",
    },
    products: normalizeProducts(detail.products),
    log,
    waypoints,
    livePosition,
    nextStop,
  };
}

export { formatTrackingDateTime, extractTollName };
